import { useEffect, useRef, useState } from 'react';
import { Toolbar } from '@/ui/Toolbar';
import { FormulaBar } from '@/grid/FormulaBar';
import { Grid } from '@/grid/Grid';
import { SheetTabs } from '@/grid/SheetTabs';
import { StatusBar } from '@/ui/StatusBar';
import { HelpPane } from '@/ui/HelpPane';
import { SchedulePane } from '@/ui/SchedulePane';
import { DatabasePane } from '@/ui/DatabasePane';
import { FilterBar } from '@/ui/FilterBar';
import { FindBar } from '@/ui/FindBar';
import { store } from '@/ui/store';
import { useStore } from '@/ui/useStore';
import { chartFromSelection } from '@/charts/chartSpec';
import { shouldConnect, useGiraffe } from '@/giraffe/useGiraffe';
import { usePersistence } from '@/giraffe/usePersistence';
import { installMcpBridge } from '@/giraffe/mcpBridge';
import { linkController } from '@/giraffe/linkController';
import { useLinkStatus } from '@/ui/useLinkStatus';
import { buildLinkedJson } from '@/data/linkedExport';
import { getCurrency } from '@/engine/format';

type Pane = 'none' | 'data' | 'help';

export default function App(): JSX.Element {
  useStore(); // subscribes this component so store.busy (below) is reactive
  const [pane, setPane] = useState<Pane>('none');
  const [finding, setFinding] = useState(false);

  // Ctrl/Cmd+F anywhere in the app. Bound at the document so it works whether
  // the grid, a pane or nothing at all has focus.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'f') {
        e.preventDefault();
        setFinding(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  const connected = shouldConnect();
  const { status } = useGiraffe(connected);
  const { status: save, saveNow } = usePersistence(connected);
  const link = useLinkStatus();

  // MCP: opt-in per browser (File menu), as it holds a socket to Giraffe's
  // relay open while the panel is. The host is read fresh per call, so a
  // workbook loaded since is the one the tools see.
  const mcpOn = store.mcpEnabled;
  useEffect(() => {
    if (!connected || !mcpOn) return;
    return installMcpBridge(
      () => ({
        wb: store.wb,
        activeSheetId: () => store.wb.activeSheetId,
        valueAt: (sheet, row, col) => store.engine.valueAt(sheet, row, col),
        evaluate: (formula, sheetName) => store.engine.evaluateFormula(formula, sheetName, 1, 1),
        data: () => store.data(),
        writeCells: (sheetId, row, col, grid) => store.writeCellsAt(sheetId, row, col, grid, 'MCP edit'),
        addSheet: (name) => store.addSheetNamed(name),
      }),
      (s) => store.setMcpStatus(s)
    );
  }, [connected, mcpOn]);

  /**
   * Keep the linked JSON current whenever the workbook itself finishes
   * saving to the project — a workbook save is the closest existing signal to
   * "settled", and piggybacking on it avoids a second independent timer. Only
   * on the pending→saved EDGE, not on every render where it happens to read
   * "saved" already.
   */
  const wasSaved = useRef(false);
  useEffect(() => {
    const justSaved = save.state === 'saved' && !wasSaved.current;
    wasSaved.current = save.state === 'saved';
    if (!justSaved || link.kind === 'unlinked' || !link.fileName) return;
    const jsonFileName = link.fileName.replace(/\.xlsx$/i, '.json');
    const source = {
      valueAt: (s: Parameters<typeof store.engine.valueAt>[0], r: number, c: number) => store.engine.valueAt(s, r, c),
      currency: getCurrency(),
    };
    const json = JSON.stringify(buildLinkedJson(store.wb, source, new Date().toISOString()), null, 2);
    void linkController.save(jsonFileName, json);
  }, [save.state, link.kind, link.fileName]);


  const toggle = (next: Pane) => () => setPane((p) => (p === next ? 'none' : next));

  return (
    <div className="gt-app">
      <Toolbar
        onOpenData={toggle('data')}
        onInsertChart={() =>
          store.upsertChart(chartFromSelection(store.newChartId(), store.sel.sheetId, store.sel, store.wb, store.engine))
        }
        onOpenHelp={toggle('help')}
        onSave={connected ? saveNow : undefined}
        save={save}
      />
      <FormulaBar />
      <FilterBar />
      {finding ? <FindBar onClose={() => setFinding(false)} /> : null}
      <div className="gt-main">
        <Grid />
        {pane === 'data' ? <SchedulePane onClose={() => setPane('none')} /> : null}
        {pane === 'help' ? <HelpPane onClose={() => setPane('none')} /> : null}
        <DatabasePane />
      </div>
      <SheetTabs />
      <StatusBar bridge={status} save={connected ? save : null} />
      {store.busy ? (
        <div className="gt-busy-overlay">
          <div className="gt-busy-spinner" />
        </div>
      ) : null}
    </div>
  );
}
